// Per-user, display-only aliases for agent sessions, keyed by agentId, never shown
// to other users. Persisted per user on the server; set/clear apply optimistically
// and a failed persist rolls the value back, so the mirror never disagrees with it.
// A session also has ONE shared name everyone sees; the viewer's alias wins on their screen.

import { agentDisplayName, type AgentNameSource } from "../agent-display.js";
import { isTransportFailure } from "../core/client.js";
import { reportUiFailure } from "../report-failure.js";

// The relay's cap (server cyborg-messages.ts; a test pins the two equal).
export const SESSION_ALIAS_MAX_LENGTH = 120;

// `workspaceId` routes the change to this person's other clients.
interface AliasClient {
  setSessionAlias(agentId: string, alias: string, workspaceId?: string): Promise<void>;
  getSessionAliases(): Promise<Record<string, string>>;
  onSessionAliasChanged(handler: (payload: { agentId: string; alias: string }) => void): () => void;
}

export class SessionAliasState {
  private aliases = $state<Record<string, string>>({});
  // Set by load(); used by set/clear to persist. Null until connected → local-only.
  private client: AliasClient | null = null;
  private unsubscribe: (() => void) | null = null;

  // The alias for an agent, or undefined when none is set (→ use the default name).
  get(agentId: string): string | undefined {
    return this.aliases[agentId];
  }

  // Hydrate from the server for the current user. Call once on connect (global,
  // not per-workspace). Stores the client so later set/clear can persist.
  // Best-effort: a failure leaves whatever is already in the mirror.
  async load(client: AliasClient): Promise<void> {
    this.client = client;
    this.unsubscribe?.();
    this.unsubscribe = client.onSessionAliasChanged((p) => this.applyRemote(p.agentId, p.alias));
    try {
      this.aliases = await client.getSessionAliases();
    } catch (err) {
      // Keep the current (possibly empty) mirror; a later load() replaces it.
      if (isTransportFailure(err)) reportUiFailure("sessionAlias.loadFailed", err);
    }
  }

  // Set/replace the alias. An empty/whitespace value clears it (back to default).
  set(agentId: string, alias: string, workspaceId?: string): void {
    const trimmed = alias.trim();
    if (!trimmed) {
      this.clear(agentId, workspaceId);
      return;
    }
    const previous = this.aliases[agentId];
    this.write(agentId, trimmed);
    this.persist(agentId, trimmed, previous, workspaceId);
  }

  // A rename or clear made on another of this person's clients (the CLI, another tab).
  // Mirror only: it was already persisted where it happened.
  applyRemote(agentId: string, alias: string): void {
    this.write(agentId, alias.trim());
  }

  clear(agentId: string, workspaceId?: string): void {
    if (!(agentId in this.aliases)) return;
    const previous = this.aliases[agentId];
    this.write(agentId, "");
    this.persist(agentId, "", previous, workspaceId);
  }

  // "" removes the entry (back to the default name).
  private write(agentId: string, alias: string | undefined): void {
    if (alias) {
      this.aliases = { ...this.aliases, [agentId]: alias };
      return;
    }
    if (!(agentId in this.aliases)) return;
    const next = { ...this.aliases };
    delete next[agentId];
    this.aliases = next;
  }

  // An empty alias is the server's delete. On failure the optimistic value is rolled
  // back, unless something newer (another edit, a remote change) already replaced it.
  // Only an unanswered request is reported: a server refusal is already on its record.
  private persist(
    agentId: string,
    alias: string,
    previous: string | undefined,
    workspaceId: string | undefined,
  ): void {
    this.client?.setSessionAlias(agentId, alias, workspaceId).catch((err: unknown) => {
      if ((this.aliases[agentId] ?? "") === alias) this.write(agentId, previous);
      if (isTransportFailure(err)) reportUiFailure("sessionAlias.persistFailed", err, { agentId });
    });
  }
}

export const sessionAlias = new SessionAliasState();

interface SharedNameRow {
  agentId: string;
  sharedName?: string | null;
}

interface SharedNameClient {
  onSessionNameChanged(
    handler: (payload: { workspaceId: string; agentId: string; name: string }) => void,
  ): () => void;
}

// The shared names of the sessions this viewer can see. Not a second store: it reads the
// roster rows, each of which carries the relay's `sharedName`.
export class SharedSessionNames {
  private rows = $state.raw<() => ReadonlyArray<SharedNameRow>>(() => []);
  private byId = $derived.by(() => {
    const names = new Map<string, string>();
    for (const row of this.rows()) {
      const name = row.sharedName?.trim();
      if (name) names.set(row.agentId, name);
    }
    return names;
  });

  private unsubscribe: (() => void) | null = null;

  // Reads the roster and applies live renames to it. Called on every connect.
  follow<T extends SharedNameRow>(
    client: SharedNameClient,
    roster: { get: () => T[]; set: (rows: T[]) => void },
  ): void {
    this.rows = roster.get;
    this.unsubscribe?.();
    this.unsubscribe = client.onSessionNameChanged((change) => {
      const rows = roster.get();
      const next = withSharedName(rows, change);
      if (next !== rows) roster.set(next);
    });
  }

  get(agentId: string): string | undefined {
    return this.byId.get(agentId);
  }
}

export const sharedSessionNames = new SharedSessionNames();

// A live rename applied to roster rows ("" clears). The same array when no row matches.
export function withSharedName<T extends SharedNameRow>(
  rows: T[],
  change: { agentId: string; name: string },
): T[] {
  if (!rows.some((row) => row.agentId === change.agentId)) return rows;
  const name = change.name.trim();
  return rows.map((row) => {
    if (row.agentId !== change.agentId) return row;
    const { sharedName: _old, ...rest } = row;
    return (name ? { ...rest, sharedName: name } : rest) as T;
  });
}

// THE session-name rule: the viewer's own alias, else the session's shared name (the roster's,
// else the row's own for a list outside the roster). Undefined when it has neither.
export function sessionOwnName(
  agent: (AgentNameSource & { sharedName?: string | null }) | null | undefined,
): string | undefined {
  const id = agent?.agentId;
  if (!id) return undefined;
  return (
    sessionAlias.get(id) ?? sharedSessionNames.get(id) ?? (agent.sharedName?.trim() || undefined)
  );
}

// THE name every surface shows for an agent session: sessionOwnName, else the surface's own
// `fallback`, else agentDisplayName. Reactive: a rename shows at once.
export function sessionDisplayName(
  agent: (AgentNameSource & { sharedName?: string | null }) | null | undefined,
  cybos?: ReadonlyArray<{ id: string; name: string }>,
  fallback?: string | null,
): string {
  return sessionOwnName(agent) ?? fallback ?? agentDisplayName(agent, cybos);
}

// An Alerts row's actor: the alias over the name stamped at raise time ("Claude"); a
// permission/error row (stamped "Someone") names the roster's agent.
export function activityActorName(
  item: { eventType: string; actorId: string | null; actorName: string; actorSessionId?: string },
  agents: ReadonlyArray<AgentNameSource & { agentId: string }>,
  cybos?: ReadonlyArray<{ id: string; name: string }>,
): string {
  // A system notice stored with no author (before machines signed their own) is Cyborg's.
  if (item.eventType === "system_notice" && (!item.actorName || item.actorName === "Someone"))
    return "Cyborg";
  const fromRoster = item.eventType === "permission_request" || item.eventType === "agent_error";
  const agent = fromRoster ? agents.find((a) => a.agentId === item.actorId) : undefined;
  // An agent row whose name was never stamped (null -> "Someone") and whose agent left the
  // roster (archived) still names an agent, never a person.
  const unnamed = fromRoster && (!item.actorName || item.actorName === "Someone");
  return agent
    ? sessionDisplayName(agent, cybos)
    : sessionDisplayName(
        { agentId: item.actorSessionId ?? item.actorId },
        undefined,
        unnamed ? agentDisplayName(null) : item.actorName,
      );
}
